import React from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { DayOfWeekData } from "@/redux/services/dashboard/dashboard.type";
import { Skeleton } from "@/components/ui/skeleton";
import {
  dashboardColors,
  dashboardShadows,
  dashboardSpacing,
} from "@/lib/theme";

interface DayOfWeekChartProps {
  data: DayOfWeekData[];
  previousData?: DayOfWeekData[];
  loading?: boolean;
}

const dayTranslations: Record<string, string> = {
  Monday: "Lundi",
  Tuesday: "Mardi",
  Wednesday: "Mercredi",
  Thursday: "Jeudi",
  Friday: "Vendredi",
  Saturday: "Samedi",
  Sunday: "Dimanche",
  monday: "Lundi",
  tuesday: "Mardi",
  wednesday: "Mercredi",
  thursday: "Jeudi",
  friday: "Vendredi",
  saturday: "Samedi",
  sunday: "Dimanche",
};

export const DayOfWeekChart: React.FC<DayOfWeekChartProps> = ({
  data,
  previousData,
  loading,
}) => {
  if (loading) {
    return (
      <div
        className="rounded-lg"
        style={{
          backgroundColor: dashboardColors.bgCard,
          padding: dashboardSpacing.cardPadding,
          boxShadow: dashboardShadows.card,
        }}
      >
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  const bestDay = data.reduce(
    (prev, current) =>
      prev.average_orders > current.average_orders ? prev : current,
    data[0]
  );

  const series: ApexAxisChartSeries = [
    {
      name: "Période actuelle",
      data: data.map((item) => Math.round(item.average_orders)),
    },
  ];

  if (previousData) {
    series.push({
      name: "Période précédente",
      data: previousData.map((item) => Math.round(item.average_orders)),
    });
  }

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: false,
      },
      fontFamily: "Inter, sans-serif",
    },
    plotOptions: {
      bar: {
        columnWidth: previousData ? "60%" : "50%",
        borderRadius: 4,
        borderRadiusApplication: "end",
        distributed: false,
      },
    },
    colors: [dashboardColors.primary, dashboardColors.success + "40"], // 40 = 25% opacity in hex
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: !!previousData,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      fontFamily: "Inter, sans-serif",
    },
    grid: {
      borderColor: dashboardColors.border,
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: data.map((item) => dayTranslations[item.day] || item.day),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: dashboardColors.textSecondary,
          fontSize: "12px",
        },
        formatter: function (value: string) {
          // Show abbreviated names on mobile
          if (window.innerWidth < 1024) {
            return value.substring(0, 3);
          }
          return value;
        },
      },
    },
    yaxis: {
      title: {
        text: "Commandes moyennes",
        style: {
          color: dashboardColors.textSecondary,
          fontSize: "12px",
          fontWeight: 500,
        },
      },
      labels: {
        style: {
          colors: dashboardColors.textSecondary,
          fontSize: "12px",
        },
      },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          yaxis: {
            title: {
              text: undefined,
            },
          },
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      custom: function ({ series, dataPointIndex }: any) {
        const day =
          dayTranslations[data[dataPointIndex].day] || data[dataPointIndex].day;
        const currentOrders = series[0][dataPointIndex];
        const previousOrders = series[1]?.[dataPointIndex];

        let tooltipHtml = `
          <div class="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
            <p class="font-medium text-gray-900 mb-2">${day}</p>
            <div class="space-y-1">
              <div class="flex items-center justify-between gap-3 text-sm">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded" style="background-color: ${dashboardColors.primary}"></div>
                  <span>Période actuelle:</span>
                </div>
                <span class="font-medium">${currentOrders} commandes/jour</span>
              </div>
        `;

        if (previousOrders !== undefined) {
          tooltipHtml += `
              <div class="flex items-center justify-between gap-3 text-sm">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded" style="background-color: ${dashboardColors.success}; opacity: 0.25"></div>
                  <span>Période précédente:</span>
                </div>
                <span class="font-medium">${previousOrders} commandes/jour</span>
              </div>
          `;
        }

        tooltipHtml += `
            </div>
          </div>
        `;

        return tooltipHtml;
      },
    },
    fill: {
      opacity: 1,
    },
  };

  if (!previousData) {
    options.colors = [dashboardColors.primary];
  }

  return (
    <div
      className="rounded-lg"
      style={{
        backgroundColor: dashboardColors.bgCard,
        padding: dashboardSpacing.cardPadding,
        boxShadow: dashboardShadows.card,
      }}
    >
      <div className="flex justify-between items-center mb-4">
        <h3
          className="text-lg font-semibold"
          style={{ color: dashboardColors.textPrimary }}
        >
          Performance par jour de la semaine
        </h3>
        <p className="text-sm" style={{ color: dashboardColors.textSecondary }}>
          Meilleur jour:{" "}
          <span
            className="font-medium"
            style={{ color: dashboardColors.primary }}
          >
            {dayTranslations[bestDay?.day] || bestDay?.day}
          </span>
        </p>
      </div>
      <div id="dayOfWeekChart">
        <ReactApexChart
          options={options}
          series={series}
          type="bar"
          height={350}
        />
      </div>
    </div>
  );
};
